import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Checkbox } from "@finance/components/ui/checkbox";
import { toast } from "sonner";
import { FloppyDisk, PaperPlaneTilt, Info } from "@phosphor-icons/react";

export default function StatementSchedule() {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/settings/statement-schedule"); setCfg(r.data); }
    catch (e) { setError(errMsg(e) || "You do not have access to these settings."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleDay = (d) => setCfg((c) => {
    const days = new Set(c.days || []);
    days.has(d) ? days.delete(d) : days.add(d);
    return { ...c, days: [...days].sort((a, b) => a - b) };
  });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/statement-schedule", {
        enabled: cfg.enabled, last_day: cfg.last_day, days: cfg.days || [], cc_accounts: cfg.cc_accounts,
      });
      setCfg(r.data);
      toast.success("Statement schedule saved");
    } catch (e) { toast.error(errMsg(e) || "Could not save the schedule."); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/clients/billwise/email-all", { cc_accounts: cfg.cc_accounts });
      toast.success(`Sent ${r.data.sent}, skipped ${r.data.skipped}, failed ${r.data.failed}`);
    } catch (e) { toast.error(errMsg(e) || "Could not send now."); }
    finally { setRunning(false); }
  };

  if (error) return (
    <Layout>
      <PageHeader title="Statement Schedule" subtitle="Automatic outstanding-statement emails to debtors." />
      <div className="p-8"><div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400" data-testid="sched-no-access">{error}</div></div>
    </Layout>
  );
  if (!cfg) return <Layout><PageHeader title="Statement Schedule" /><div className="p-8 text-sm text-gray-400">Loading…</div></Layout>;

  return (
    <Layout>
      <PageHeader title="Statement Schedule" subtitle="Auto-email debtors their outstanding bill-wise statement on the days you choose each month." />
      <div className="max-w-3xl space-y-5 p-8">
        <div className="flex items-center gap-2 rounded-sm border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm text-blue-800">
          <Info size={18} /> Emails go out at 18:00 EAT on each selected day, to every client with outstanding invoices and an email on file. Fully-settled clients are skipped.
        </div>

        <div className="space-y-4 rounded-sm border border-gray-200 bg-white p-5">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <Checkbox checked={cfg.enabled} onCheckedChange={(v) => set("enabled", !!v)} data-testid="sched-enabled" />
            Automatic statement emails enabled
          </label>

          <div className={cfg.enabled ? "" : "pointer-events-none opacity-40"}>
            <div className="lx-label mb-2">Days of the month to send</div>
            <div className="grid grid-cols-7 gap-2" data-testid="sched-days">
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <button key={d} type="button" data-testid={`sched-day-${d}`}
                  onClick={() => toggleDay(d)}
                  className={`h-9 rounded-sm border text-sm font-medium transition-colors ${(cfg.days || []).includes(d) ? "border-[#1B4383] bg-[#1B4383] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>{d}</button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">Days 29–31 vary by month — use "last day of every month" below to cover month-end.</p>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
              <Checkbox checked={cfg.last_day} onCheckedChange={(v) => set("last_day", !!v)} data-testid="sched-lastday" />
              Also send on the last day of every month
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox checked={cfg.cc_accounts} onCheckedChange={(v) => set("cc_accounts", !!v)} data-testid="sched-cc" />
            CC the accounts team on every statement (applies to scheduled and manual sends)
          </label>

          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" onClick={runNow} disabled={running} data-testid="sched-run-now">
              <PaperPlaneTilt size={16} className="mr-1" />{running ? "Sending…" : "Send now"}
            </Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" onClick={save} disabled={saving} data-testid="sched-save">
              <FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Save schedule"}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
